import type { Uuid } from '@sibe/utilities/common';

import { HsMatrix4 } from '../../../math-new/objects/matrix4.class';
import type { HsHypersceneTypes } from '../../../math-new/types/hyperscene-types.type';

export abstract class HsObject3D {
  public readonly worldMatrix: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
  public readonly localMatrix: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
  public parent: HsObject3D | null = null;
  public children: Map<Uuid, HsObject3D> = new Map<Uuid, HsObject3D>();
  public isDeleted: boolean = false;

  static readonly #matrix4Temp1: HsMatrix4 = HsMatrix4.makeIdentityMatrix();

  constructor(public readonly uuid: Uuid, public name: string) {}

  /**
   * Copies to world matrix.
   * @param matrixValues matrix values.
   * @returns Result value.
   */
  public copyToWorldMatrix(matrixValues: HsHypersceneTypes.Array16): HsObject3D {
    this.worldMatrix.setFromArray(matrixValues);
    return this;
  }

  /**
   * Copies to local matrix.
   * @param matrixValues matrix values.
   * @returns Result value.
   */
  public copyToLocalMatrix(matrixValues: HsHypersceneTypes.Array16): HsObject3D {
    this.localMatrix.setFromArray(matrixValues);
    return this;
  }

  /**
   * Copies to world matrix with update.
   * @param matrixValues matrix values.
   * @returns Result value.
   */
  public copyToWorldMatrixWithUpdate(matrixValues: HsHypersceneTypes.Array16): HsObject3D {
    this.worldMatrix.setFromArray(matrixValues);
    return this.updateLocalMatrix();
  }

  /**
   * Copies to local matrix with update.
   * @param matrixValues matrix values.
   * @returns Result value.
   */
  public copyToLocalMatrixWithUpdate(matrixValues: HsHypersceneTypes.Array16): HsObject3D {
    this.localMatrix.setFromArray(matrixValues);
    return this.updateWorldMatrix();
  }

  /**
   * Updates world matrix.
   * @returns Result value.
   */
  public updateWorldMatrix(): HsObject3D {
    this.parent === null
      ? this.worldMatrix.copy(this.localMatrix)
      : this.worldMatrix.multiplyMatrices(this.parent.worldMatrix, this.localMatrix);
    return this;
  }

  /**
   * Updates local matrix.
   * @returns Result value.
   */
  public updateLocalMatrix(): HsObject3D {
    if (this.parent === null) {
      this.localMatrix.copy(this.worldMatrix);
      return this;
    }
    HsObject3D.#matrix4Temp1.copy(this.parent.worldMatrix).inverse();
    this.localMatrix.multiplyMatrices(HsObject3D.#matrix4Temp1, this.worldMatrix);
    return this;
  }

  /**
   * Updates children world matrix.
   * @returns Result value.
   */
  public updateChildrenWorldMatrix(): HsObject3D {
    const childrenValues: IterableIterator<HsObject3D> = this.children.values();
    for (let i: number = 0; i < this.children.size; i++) {
      const element: IteratorResult<HsObject3D> = childrenValues.next();
      const child: HsObject3D = element.value;
      child.updateWorldMatrix().updateChildrenWorldMatrix();
    }
    return this;
  }

  /**
   * Sets parent.
   * @param parent parent.
   * @returns Result value.
   */
  public setParent(parent: HsObject3D | null): HsObject3D {
    this.parent = parent;
    if (this.parent !== null) {
      this.parent.children.set(this.uuid, this);
    }
    return this.updateLocalMatrix();
  }

  /**
   * Adds child.
   * @param child child.
   * @returns Result value.
   */
  public addChild(child: HsObject3D): HsObject3D {
    child.setParent(this);
    return this;
  }

  /**
   * Adds children.
   * @param children children.
   * @returns Result value.
   */
  public addChildren(children: HsObject3D[]): HsObject3D {
    for (let i: number = 0; i < children.length; i++) {
      this.addChild(children[i]);
    }
    return this;
  }

  /**
   * Removes child.
   * @param childId child id.
   * @returns Result value.
   */
  public removeChild(childId: Uuid): HsObject3D {
    const child: HsObject3D | undefined = this.children.get(childId);
    if (child !== undefined) {
      child.setParent(null);
      this.children.delete(childId);
    }
    return this;
  }

  /**
   * Removes children.
   * @param childrenIds children ids.
   * @returns Result value.
   */
  public removeChildren(childrenIds: Uuid[]): HsObject3D {
    for (let i: number = 0; i < childrenIds.length; i++) {
      this.removeChild(childrenIds[i]);
    }
    return this;
  }

  /**
   * Handles children.
   * @returns Result value.
   */
  public clearChildren(): HsObject3D {
    const childrenValues: IterableIterator<HsObject3D> = this.children.values();
    for (let i: number = 0; i < this.children.size; i++) {
      const element: IteratorResult<HsObject3D> = childrenValues.next();
      const child: HsObject3D = element.value;
      child.setParent(null);
    }
    this.children.clear();
    return this;
  }
}
